/**
 * Simple Configuration Initializer
 *
 * A non-blocking configuration initializer that loads config in the background
 * while allowing the app to continue loading.
 */

import React, { useEffect, useState } from "react";
import { logApp } from "@/utils/logApp";
import config, { onConfigChange, type ResolvedConfig } from "../config";

interface ConfigProviderProps {
	children: React.ReactNode;
}

const ConfigurationContext = React.createContext<ResolvedConfig | null>(null);

/**
 * Simple configuration provider that doesn't block app loading
 */
export function ConfigProvider({ children }: ConfigProviderProps) {
	const [currentConfig, setCurrentConfig] = useState<ResolvedConfig | null>(
		null,
	);

	useEffect(() => {
		// Initialize configuration in background
		async function initConfig() {
			try {
				// console.log("🔧 Config: Initializing in background...");
				const configData = await config.initialize();
				setCurrentConfig(configData);
				// console.log("✅ Config: Configuration loaded successfully");
			} catch (error) {
				console.error("❌ Config: Failed to load configuration:", error);
				// Continue without config - components can handle null config
				logApp({
					level: "error",
					message: `ConfigProvider: Failed to load configuration: ${error}`,
				});
			}
		}

		initConfig();

		// Listen for config changes
		const unsubscribe = onConfigChange((newConfig) => {
			console.log("🔄 Config: Configuration changed");
			setCurrentConfig(newConfig);
		});

		return unsubscribe;
	}, []);

	return (
		<ConfigurationContext.Provider value={currentConfig}>
			{children}
		</ConfigurationContext.Provider>
	);
}

/**
 * Hook to access configuration (returns null if not loaded yet)
 */
export function useConfiguration(): ResolvedConfig | null {
	return React.useContext(ConfigurationContext);
}

/**
 * Hook to check if a feature is enabled (safe with null config)
 * Supports both "flags" and "features" for backward compatibility
 */
export function useFeatureFlag(
	feature: keyof ResolvedConfig["features"],
): boolean {
	const config = useConfiguration();
	// Try flags first (new), then features (backward compatibility)
	return config?.flags?.[feature] ?? config?.features?.[feature] ?? false;
}

/**
 * Hook to get API endpoint URL (safe with null config)
 */
export function useApiEndpoint(
	endpoint: keyof ResolvedConfig["api"]["endpoints"],
): string {
	const config = useConfiguration();
	if (!config) {
		throw new Error("Configuration not loaded: cannot resolve API endpoint");
	}
	return `${config.api.baseUrl}${config.api.endpoints[endpoint]}`;
}

/**
 * Hook to get environment information (safe with null config)
 */
export function useEnvironment() {
	const config = useConfiguration();

	return {
		environment: config?.app.environment ?? "development",
		isLocal: config?.app.environment === "local" || false,
		isDevelopment: config?.app.environment === "development" || false,
		isProduction: config?.app.environment === "production" || false,
		isQA: config?.app.environment === "qa" || false,
	};
}

/**
 * Configuration context type for router
 * These functions can be safely used in router hooks like beforeLoad
 */
export interface ConfigRouterContext {
	getConfig: () => ResolvedConfig | null;
	isFeatureEnabled: (feature: keyof ResolvedConfig["features"]) => boolean;
	getApiEndpoint: (
		endpoint: keyof ResolvedConfig["api"]["endpoints"],
	) => string;
	getApiBaseUrl: () => string;
	isDevelopment: () => boolean;
	isProduction: () => boolean;
	isLocal: () => boolean;
	getEnvironment: () => string;
	getAppConfig: () => {
		name: string;
		version: string;
		title: string;
		environment: string;
	};
}

/**
 * Create router-safe config functions from current config state
 * This is similar to how AuthProvider creates its context value
 */
export function createConfigRouterContext(
	config: ResolvedConfig | null,
): ConfigRouterContext {
	return {
		getConfig: () => config,

		isFeatureEnabled: (feature: keyof ResolvedConfig["features"]): boolean => {
			// Try flags first (new), then features (backward compatibility)
			return config?.flags?.[feature] ?? config?.features?.[feature] ?? false;
		},

		getApiEndpoint: (
			endpoint: keyof ResolvedConfig["api"]["endpoints"],
		): string => {
			if (!config) {
				throw new Error(
					"Configuration not loaded: cannot resolve API endpoint",
				);
			}
			return `${config.api.baseUrl}${config.api.endpoints[endpoint]}`;
		},

		getApiBaseUrl: (): string => {
			if (!config) {
				throw new Error(
					"Configuration not loaded: cannot resolve API base URL",
				);
			}
			return config.api.baseUrl;
		},

		isDevelopment: (): boolean => {
			return config?.app.environment === "development" || false;
		},

		isProduction: (): boolean => {
			return config?.app.environment === "production" || false;
		},

		isLocal: (): boolean => {
			return config?.app.environment === "local" || false;
		},

		getEnvironment: (): string => {
			return config?.app.environment ?? "development";
		},

		getAppConfig: () => {
			return {
				name: config?.app.name ?? "Fawkes Web Application",
				version: config?.app.version ?? "1.0.0",
				title: config?.app.title ?? "Fawkes",
				environment: config?.app.environment ?? "development",
			};
		},
	};
}

export { ConfigurationContext };
